import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE, getPhone, BackBar, Chip, Card, PrimaryBtn } from './healthShared';

const DIETS = ['Vegetarian', 'Non-vegetarian', 'Eggetarian', 'Vegan'];
const ACTIVITY = ['Sedentary', 'Lightly active', 'Moderately active', 'Very active'];
const CONDITIONS = ['None', 'Hypertension', 'Diabetes / Prediabetes', 'Thyroid', 'PCOS', 'High cholesterol', 'Back / joint pain'];
const GOALS = ['Lose weight', 'Build muscle', 'Improve fitness', 'Improve energy', 'Improve sleep', 'Improve overall health'];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-bold tracking-wide uppercase text-[#66735F] mb-2">{label}</label>
    {children}
  </div>
);

const Num = ({ value, onChange, placeholder, testId, unit }) => (
  <div className="relative">
    <input
      type="number"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full px-4 py-3 rounded-2xl bg-white border border-[#E3E5D8] text-[#273127] font-semibold focus:outline-none focus:border-[#7FA183] focus:ring-2 focus:ring-[#7FA183]/20"
    />
    {unit && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-[#9AA594] font-semibold">{unit}</span>}
  </div>
);

export default function HealthAssessment({ profile, onSaved, onBack }) {
  const [form, setForm] = useState({
    age: profile?.age || '',
    sex: profile?.sex || '',
    height_cm: profile?.height_cm || '',
    weight_kg: profile?.weight_kg || '',
    waist_cm: profile?.waist_cm || '',
    diet: profile?.diet || '',
    activity: profile?.activity || '',
    sleep_hours: profile?.sleep_hours || '',
    conditions: profile?.conditions || [],
    goal: profile?.goal || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleCondition = (c) => {
    setForm((f) => {
      if (c === 'None') return { ...f, conditions: [] };
      const has = f.conditions.includes(c);
      return { ...f, conditions: has ? f.conditions.filter((x) => x !== c) : [...f.conditions, c] };
    });
  };

  const submit = async () => {
    if (!form.age || !form.sex || !form.height_cm || !form.weight_kg || !form.goal) {
      toast.error('Please fill age, sex, height, weight and your goal');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE}/api/health-hub/profile`, {
        phone: getPhone(),
        age: Number(form.age),
        sex: form.sex,
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        waist_cm: form.waist_cm ? Number(form.waist_cm) : null,
        diet: form.diet || 'Vegetarian',
        activity: form.activity || 'Sedentary',
        sleep_hours: form.sleep_hours ? Number(form.sleep_hours) : 7,
        conditions: form.conditions,
        goal: form.goal,
      });
      toast.success('Your health snapshot is ready');
      onSaved(res.data.profile, res.data.snapshot);
    } catch {
      toast.error('Could not save. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div data-testid="health-assessment">
      <BackBar title="Quick Health Assessment" onBack={onBack} />
      <p className="text-sm text-[#66735F] mb-6 -mt-2">Takes under a minute. We use this to personalise everything.</p>
      <Card className="p-5 sm:p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Age"><Num value={form.age} onChange={(v) => set('age', v)} placeholder="35" testId="assess-age" unit="yrs" /></Field>
          <Field label="Sex">
            <div className="flex gap-2">
              {['Male', 'Female'].map((s) => (
                <Chip key={s} active={form.sex === s} onClick={() => set('sex', s)} testId={`assess-sex-${s.toLowerCase()}`}>{s}</Chip>
              ))}
            </div>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Height"><Num value={form.height_cm} onChange={(v) => set('height_cm', v)} placeholder="165" testId="assess-height" unit="cm" /></Field>
          <Field label="Weight"><Num value={form.weight_kg} onChange={(v) => set('weight_kg', v)} placeholder="70" testId="assess-weight" unit="kg" /></Field>
          <Field label="Waist"><Num value={form.waist_cm} onChange={(v) => set('waist_cm', v)} placeholder="86" testId="assess-waist" unit="cm" /></Field>
        </div>
        <Field label="Dietary preference">
          <div className="flex flex-wrap gap-2">
            {DIETS.map((d) => <Chip key={d} active={form.diet === d} onClick={() => set('diet', d)} testId={`assess-diet-${d.toLowerCase().replace(/[^a-z]/g, '')}`}>{d}</Chip>)}
          </div>
        </Field>
        <Field label="Activity level">
          <div className="flex flex-wrap gap-2">
            {ACTIVITY.map((a) => <Chip key={a} active={form.activity === a} onClick={() => set('activity', a)} testId={`assess-activity-${a.split(' ')[0].toLowerCase()}`}>{a}</Chip>)}
          </div>
        </Field>
        <Field label="Average sleep per night">
          <div className="flex flex-wrap gap-2">
            {['<5', '5', '6', '7', '8+'].map((h) => {
              const val = h === '<5' ? 4.5 : h === '8+' ? 8 : Number(h);
              return <Chip key={h} active={Number(form.sleep_hours) === val} onClick={() => set('sleep_hours', val)} testId={`assess-sleep-${h.replace(/[<+]/g, '')}`}>{h} hrs</Chip>;
            })}
          </div>
        </Field>
        <Field label="Health conditions (select all that apply)">
          <div className="flex flex-wrap gap-2">
            {CONDITIONS.map((c) => (
              <Chip key={c} active={c === 'None' ? form.conditions.length === 0 : form.conditions.includes(c)} onClick={() => toggleCondition(c)} testId={`assess-cond-${c.split(' ')[0].toLowerCase().replace(/[^a-z]/g, '')}`}>{c}</Chip>
            ))}
          </div>
        </Field>
        <Field label="Main goal">
          <div className="flex flex-wrap gap-2">
            {GOALS.map((g) => <Chip key={g} active={form.goal === g} onClick={() => set('goal', g)} testId={`assess-goal-${g.split(' ')[1]?.toLowerCase() || g.toLowerCase()}`}>{g}</Chip>)}
          </div>
        </Field>
        <PrimaryBtn onClick={submit} disabled={saving} testId="assess-submit-btn" className="w-full text-base py-4">
          {saving ? 'Creating your snapshot…' : 'CREATE MY HEALTH SNAPSHOT'}
        </PrimaryBtn>
        <p className="text-[11px] text-[#9AA594] text-center">Wellness guidance only — not a medical diagnosis.</p>
      </Card>
    </div>
  );
}
